import React, { useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { HiOutlineMicrophone, HiPaperAirplane } from 'react-icons/hi';

const WELCOME_FEATURES = [
  { icon: '🔍', title: 'Drill-Down Follow-ups', desc: 'click suggested follow-up queries after every response' },
  { icon: '📥', title: 'Export to Excel', desc: 'download any result table with one click' },
  { icon: '🔥', title: 'Toil Index Dashboard', desc: "type 'show toil dashboard' to see repeat tickets, consultant load & high-volume types" },
  { icon: '📊', title: 'Confidence Score', desc: 'every response shows a 0–100 quality score with breakdown tooltip' },
  { icon: '🔡', title: 'Column Aliasing', desc: 'teach ARIA shortcuts (e.g., mapping user terms to database fields)' },
  { icon: '🎙️', title: 'Voice Input', desc: 'click the mic button to speak your query' },
  { icon: '🔢', title: 'Token Counter', desc: 'input/output token usage shown at the bottom' },
];

function formatTime(date) {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function NlpChatView({
  messages,
  isLoading,
  loadingLabel,
  message,
  onMessageChange,
  onSend,
  onKeyDown,
  inputRef,
}) {
  const messagesEndRef = useRef(null);
  const welcomeTime = formatTime(new Date());

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const showWelcome = messages.length <= 1 && messages[0]?.type === 'bot' && !isLoading;

  return (
    <div className="nlp-chat-view">
      <div className="nlp-messages">
        <div className="nlp-messages-inner">
          {showWelcome && (
            <div className="nlp-welcome-row">
              <div className="nlp-bot-avatar" aria-hidden="true">B</div>
              <div className="nlp-welcome-card">
                <span className="nlp-welcome-time">{welcomeTime}</span>
                <h2 className="nlp-welcome-title">Welcome to BAInocular — NLP Engine.</h2>
                <p className="nlp-welcome-heading">New in v5:</p>
                <ul className="nlp-feature-list">
                  {WELCOME_FEATURES.map((f) => (
                    <li key={f.title}>
                      <span className="nlp-feature-icon" aria-hidden="true">{f.icon}</span>
                      <span>
                        <strong>{f.title}</strong>
                        {' — '}
                        {f.desc}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="nlp-welcome-footer">
                  Open the 🧠 Memory panel to add context before asking questions.
                </p>
              </div>
            </div>
          )}

          {messages.map((msg, idx) => {
            if (showWelcome && idx === 0) return null;
            return (
              <div
                key={idx}
                className={`nlp-message-row ${msg.type === 'user' ? 'user' : 'bot'}`}
              >
                {msg.type === 'bot' && (
                  <div className="nlp-bot-avatar nlp-bot-avatar-sm" aria-hidden="true">B</div>
                )}
                <div className={`nlp-message-bubble ${msg.isError ? 'error' : ''}`}>
                  {msg.type === 'user' ? (
                    <span>{msg.text}</span>
                  ) : (
                    <div className="nlp-markdown">
                      <ReactMarkdown
                        components={{
                          a: ({ node, ...props }) => (
                            <a {...props} target="_blank" rel="noopener noreferrer" />
                          ),
                        }}
                      >
                        {msg.text}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {isLoading && (
            <div className="nlp-message-row bot">
              <div className="nlp-bot-avatar nlp-bot-avatar-sm" aria-hidden="true">B</div>
              <div className="nlp-message-bubble loading">
                <span className="nlp-spinner" aria-hidden="true" />
                <span>{loadingLabel}</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <form className="nlp-input-area" onSubmit={onSend}>
        <div className="nlp-input-inner">
          <div className="nlp-input-row">
            <textarea
              ref={inputRef}
              className="nlp-textarea"
              value={message}
              onChange={onMessageChange}
              onKeyDown={onKeyDown}
              placeholder="Ask a question about your SAP ticket data..."
              disabled={isLoading}
              rows={1}
            />
            <button
              type="button"
              className="nlp-mic-btn"
              aria-label="Voice input"
              title="Voice input"
              disabled={isLoading}
            >
              <HiOutlineMicrophone size={20} />
            </button>
            <button
              type="submit"
              className="nlp-send-btn"
              disabled={isLoading || !message.trim()}
              aria-label="Send message"
            >
              <HiPaperAirplane size={18} />
            </button>
          </div>
          <p className="nlp-input-hint">
            Press <kbd>Enter</kbd> to send · <kbd>Shift+Enter</kbd> for new line
          </p>
        </div>
      </form>
    </div>
  );
}

export default NlpChatView;
